import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Search, ArrowUpRight, ArrowDownRight, DollarSign,
  Calendar, FileText, CheckCircle2, AlertCircle, Trash2, ArrowLeftRight, Undo2
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";
import { gerarRelatorioFinanceiroPDF, gerarRelatorioConsolidadoPDF, gerarRelatorioPorClientePDF } from "@/lib/financialReportPDF";
import { formatCurrency } from "@/components/financeiro/contas/utils";
import { LancamentosTable } from "@/components/financeiro/contas/LancamentosTable";
import { LancamentoForm } from "@/components/financeiro/contas/LancamentoForm";
import {
  ViewLancamentoDialog, DeleteLancamentoDialog, BulkDeleteDialog,
  BaixaDialog, TransferenciaDialog, PdfPreviewDialog,
} from "@/components/financeiro/contas/ContasDialogs";


function ContasPage({ tipo }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showBaixaDialog, setShowBaixaDialog] = useState(false);
  const [baixaItem, setBaixaItem] = useState(null);
  const [reverterItem, setReverterItem] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkPassword, setBulkPassword] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, blobUrl: null, filename: null });
  const [baixaData, setBaixaData] = useState({
    data_pagamento: new Date().toISOString().split('T')[0],
    valor_pago: 0,
    juros: 0,
    multa: 0,
    desconto: 0,
    banco_id: "",
    observacao_baixa: ""
  });
  
  // Filtro de período - padrão: últimos 30 dias até próximos 60 dias
  const [dataInicio, setDataInicio] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 60);
    return date.toISOString().split('T')[0];
  });
  const [totais, setTotais] = useState({
    total: 0,
    pendente: 0,
    pago: 0,
    vencido: 0
  });

  const isReceber = tipo === "receber";
  const tipoLancamento = isReceber ? "receita" : "despesa";

  // Ranking + filtro por entidade (cliente/fornecedor)
  const [ranking, setRanking] = useState([]);
  const [filtroEntidade, setFiltroEntidade] = useState("");
  const [filtroSemConta, setFiltroSemConta] = useState(false);
  // Transferência entre contas próprias
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferData, setTransferData] = useState({
    banco_origem_id: "", banco_destino_id: "", valor: "", data: new Date().toISOString().split('T')[0], observacao: ""
  });
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchDependencies();
    fetchRanking();
  }, [filterStatus, tipo, dataInicio, dataFim]);

  const fetchRanking = async () => {
    try {
      const r = await axios.get(`${API}/financeiro/ranking-entidades`, {
        params: { tipo: tipoLancamento, data_inicio: dataInicio, data_fim: dataFim, apenas_pendentes: true }
      });
      setRanking(r.data?.entidades || []);
    } catch {
      setRanking([]);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { 
        tipo: tipoLancamento,
        data_inicio: dataInicio,
        data_fim: dataFim
      };
      if (filterStatus && filterStatus !== "all") {
        params.status = filterStatus;
      }
      const response = await axios.get(`${API}/financeiro/lancamentos`, { params });
      const lancamentos = response.data.lancamentos || [];
      setItems(lancamentos);
      
      // Calcular totais
      const hoje = new Date().toISOString().split('T')[0];
      const total = lancamentos.reduce((acc, l) => acc + (l.valor || 0), 0);
      const pendente = lancamentos.filter(l => l.status === "pendente").reduce((acc, l) => acc + (l.valor || 0), 0);
      const pago = lancamentos.filter(l => l.status === "pago").reduce((acc, l) => acc + (l.valor || 0), 0);
      const vencido = lancamentos.filter(l => l.status === "pendente" && l.data_vencimento < hoje).reduce((acc, l) => acc + (l.valor || 0), 0);
      
      setTotais({ total, pendente, pago, vencido });
    } catch (error) {
      toast.error("Erro ao carregar lançamentos");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [centrosRes, contasRes, catRes, bancosRes] = await Promise.all([
        axios.get(`${API}/centros-custo?ativo=true`),
        axios.get(`${API}/plano-contas?tipo=${tipoLancamento}&ativo=true`),
        axios.get(`${API}/categorias?tipo=${tipoLancamento}`),
        axios.get(`${API}/bancos?ativo=true`),
      ]);
      setCentrosCusto(centrosRes.data);
      setContas(contasRes.data);
      setCategorias(catRes.data || []);
      setBancos(bancosRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar dependências:", error);
    }
  };

  const handleDelete = async (id, password) => {
    try {
      await axios.delete(`${API}/lancamentos/${id}`, { data: { password }, skipAuthInterceptor: true });
      toast.success("Lançamento excluído com sucesso!");
      setShowDeleteDialog(false);
      setDeleteItem(null);
      setDeletePassword("");
      fetchItems();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao excluir"));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem || !deletePassword) return;
    setDeleting(true);
    try {
      await handleDelete(deleteItem.id, deletePassword);
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkPassword || selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await axios.post(`${API}/lancamentos/bulk-delete`,
        { password: bulkPassword, ids },
        { skipAuthInterceptor: true });
      const { removidos, falhas } = r.data || {};
      if (removidos > 0) {
        toast.success(`${removidos} ${removidos === 1 ? "lançamento removido" : "lançamentos removidos"}`);
      }
      if (falhas && falhas.length > 0) {
        toast.warning(`${falhas.length} não puderam ser excluídos (ex.: ${falhas[0].motivo})`);
      }
      setSelectedIds(new Set());
      setShowBulkDelete(false);
      setBulkPassword("");
      fetchItems();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir em lote"));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBaixa = async () => {
    if (!baixaItem) return;
    try {
      const body = {
        data_pagamento: baixaData.data_pagamento,
        valor_pago: parseFloat(baixaData.valor_pago) || 0,
        juros: parseFloat(baixaData.juros) || 0,
        multa: parseFloat(baixaData.multa) || 0,
        desconto: parseFloat(baixaData.desconto) || 0,
      };
      if (baixaData.observacao_baixa) body.observacao_baixa = baixaData.observacao_baixa;
      if (baixaData.banco_id) body.banco_id = baixaData.banco_id;
      const r = await axios.post(`${API}/lancamentos/${baixaItem.id}/baixar`, body);
      const residuo = r.data?.residuo || 0;
      if (residuo > 0) {
        toast.success(`Baixa parcial realizada! Resíduo de ${formatCurrency(residuo)} gerado como novo lançamento pendente.`);
      } else {
        toast.success("Baixa realizada com sucesso!");
      }
      setShowBaixaDialog(false);
      setBaixaItem(null);
      fetchItems();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao realizar baixa"));
    }
  };

  const handleReverterBaixa = async () => {
    if (!reverterItem) return;
    setReverting(true);
    try {
      const r = await axios.post(`${API}/lancamentos/${reverterItem.id}/reverter-baixa`, {});
      toast.success(r.data?.message || "Baixa revertida — lançamento voltou para Pendente");
      if (r.data?.residuo_removido) {
        toast.info("O resíduo gerado pela baixa parcial também foi removido.");
      }
      setReverterItem(null);
      fetchItems();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao reverter baixa"));
    } finally {
      setReverting(false);
    }
  };

  const openBaixaDialog = (item) => {
    setBaixaItem(item);
    const corr = item.correcao;
    setBaixaData({
      data_pagamento: new Date().toISOString().split('T')[0],
      valor_pago: corr ? corr.valor_corrigido : item.valor,
      juros: corr ? corr.juros_calculado : 0,
      multa: corr ? corr.multa_calculada : 0,
      desconto: 0,
      banco_id: item.banco_id || "",
      observacao_baixa: corr ? `Correção automática: ${corr.dias_atraso}d de atraso (juros ${corr.juros_calculado.toFixed(2)} + multa ${corr.multa_calculada.toFixed(2)})` : ""
    });
    setShowBaixaDialog(true);
  };

  // Check for overdue items
  const hoje = new Date().toISOString().split('T')[0];
  const itemsWithStatus = items.map(item => ({
    ...item,
    status_display: item.status === "pendente" && item.data_vencimento < hoje ? "vencido" : item.status
  }));  const filteredItems = itemsWithStatus.filter(item => {    // Filtro "baixa sem conta identificada" (legado da migracao F1)
    if (filtroSemConta && !(item.baixa_sem_conta && !item.banco_id)) return false;
    // Filtro por entidade (cliente/fornecedor) selecionada no ranking
    if (filtroEntidade) {
      const entId = item.fornecedor_id || item.cliente_id;
      // Se nao tem fornecedor_id direto, comparamos via cliente_nome (resolvido por contrato)
      if (entId !== filtroEntidade) {
        const ent = ranking.find(r => r.id === filtroEntidade);
        if (!ent || item.cliente_nome !== ent.nome) return false;
      }
    }
    const q = (searchTerm || "").toLowerCase().trim();
    if (!q) return true;
    return (
      item.descricao?.toLowerCase().includes(q) ||
      item.documento?.toLowerCase().includes(q) ||
      item.cliente_nome?.toLowerCase().includes(q) ||
      String(item.proposta_numero || "").toLowerCase().includes(q) ||
      item.parcela?.toLowerCase?.().includes(q) ||
      item.observacao?.toLowerCase().includes(q)
    );
  });

  // Generate PDF report for current filters
  const handleGerarPDF = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    const toastId = toast.loading("Gerando relatório em PDF…");
    try {
      const entidadeNome = filtroEntidade ? (ranking.find(r => r.id === filtroEntidade)?.nome || "") : "";
      const { blob, filename } = await gerarRelatorioFinanceiroPDF({
        tipo,
        items: filteredItems,
        filters: {
          dataInicio,
          dataFim,
          status: filterStatus,
          entidadeNome,
          search: searchTerm,
        },
        totais,
      });
      const blobUrl = URL.createObjectURL(blob);
      setPdfModal(prev => {
        if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return { open: true, blobUrl, filename };
      });
      toast.success("PDF gerado — visualize ou baixe na janela aberta.", { id: toastId, duration: 4000 });
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      toast.error("Falha ao gerar PDF. Tente novamente.", { id: toastId });
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Generate PDF grouped by cliente/fornecedor (subtotais por entidade)
  const handleGerarPDFPorCliente = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    const toastId = toast.loading(`Gerando relatório por ${isReceber ? "cliente" : "fornecedor"}…`);
    try {
      const { blob, filename } = await gerarRelatorioPorClientePDF({
        tipo,
        items: filteredItems,
        filters: { dataInicio, dataFim, status: filterStatus },
        totais,
      });
      const blobUrl = URL.createObjectURL(blob);
      setPdfModal(prev => {
        if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return { open: true, blobUrl, filename };
      });
      toast.success("PDF agrupado gerado — visualize ou baixe na janela aberta.", { id: toastId, duration: 4000 });
    } catch (err) {
      console.error("Erro ao gerar PDF por cliente:", err);
      toast.error("Falha ao gerar PDF. Tente novamente.", { id: toastId });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const closePdfModal = () => {
    setPdfModal(prev => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, blobUrl: null, filename: null };
    });
  };

  // Generate CONSOLIDATED PDF (pagar + receber together)
  const handleGerarPDFConsolidado = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    const toastId = toast.loading("Gerando relatório consolidado em PDF…");
    try {
      const params = { data_inicio: dataInicio, data_fim: dataFim };
      if (filterStatus && filterStatus !== "all") params.status = filterStatus;
      const response = await axios.get(`${API}/financeiro/lancamentos`, { params });
      const todos = response.data.lancamentos || [];
      const { blob, filename } = await gerarRelatorioConsolidadoPDF({
        items: todos,
        filters: { dataInicio, dataFim, status: filterStatus },
      });
      const blobUrl = URL.createObjectURL(blob);
      setPdfModal(prev => {
        if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return { open: true, blobUrl, filename };
      });
      toast.success("PDF consolidado gerado — visualize ou baixe na janela aberta.", { id: toastId, duration: 4000 });
    } catch (err) {
      console.error("Erro ao gerar PDF consolidado:", err);
      toast.error("Falha ao gerar PDF consolidado. Tente novamente.", { id: toastId });
    } finally {
      setGeneratingPdf(false);
    }
  };


  const handleTransferencia = async () => {
    if (!transferData.banco_origem_id || !transferData.banco_destino_id) {
      toast.error("Selecione as contas de origem e destino"); return;
    }
    if (transferData.banco_origem_id === transferData.banco_destino_id) {
      toast.error("Origem e destino devem ser contas diferentes"); return;
    }
    const valor = parseFloat(transferData.valor);
    if (!valor || valor <= 0) { toast.error("Informe um valor válido"); return; }
    setTransferring(true);
    try {
      const payload = {
        banco_origem_id: transferData.banco_origem_id,
        banco_destino_id: transferData.banco_destino_id,
        valor, data: transferData.data,
      };
      if (transferData.observacao) payload.observacao = transferData.observacao;
      await axios.post(`${API}/transferencias`, payload);
      toast.success("Transferência registrada! (fora do DRE, baixada nas duas contas)");
      setShowTransfer(false);
      setTransferData({ banco_origem_id: "", banco_destino_id: "", valor: "", data: new Date().toISOString().split('T')[0], observacao: "" });
      fetchItems();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar transferência"));
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={`contas-${tipo}-page`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
            Contas a {isReceber ? "Receber" : "Pagar"}
          </h1>
          <p className="text-slate-500">
            {isReceber ? "Receitas e valores a receber" : "Despesas e valores a pagar"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-luvcorp-navy text-luvcorp-navy hover:bg-luvcorp-navy/5"
            onClick={() => setShowTransfer(true)}
            data-testid="btn-transferencia"
          >
            <ArrowLeftRight className="h-4 w-4 mr-2" />
            Transferência entre Contas
          </Button>
          <Button 
            className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white"
            onClick={() => { setEditItem(null); setShowForm(true); }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Lançamento
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-slate-200 rounded-full flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total no Período</p>
                <p className="text-xl font-bold text-slate-700">{formatCurrency(totais.total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-0 shadow-sm border-l-4 ${isReceber ? 'border-l-green-500' : 'border-l-red-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isReceber ? 'bg-green-100' : 'bg-red-100'}`}>
                {isReceber ? <ArrowUpRight className="h-5 w-5 text-green-600" /> : <ArrowDownRight className="h-5 w-5 text-red-600" />}
              </div>
              <div>
                <p className="text-sm text-slate-500">Pendente</p>
                <p className={`text-xl font-bold ${isReceber ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(totais.pendente)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">{isReceber ? "Recebido" : "Pago"}</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(totais.pago)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Vencido</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(totais.vencido)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Período */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pb-3 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-luvcorp-navy" />
                <span className="text-sm font-medium text-slate-700">Período:</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="data-inicio" className="text-sm text-slate-500 whitespace-nowrap">De</Label>
                  <Input
                    id="data-inicio"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-[150px]"
                    data-testid="filtro-data-inicio"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="data-fim" className="text-sm text-slate-500 whitespace-nowrap">até</Label>
                  <Input
                    id="data-fim"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-[150px]"
                    data-testid="filtro-data-fim"
                  />
                </div>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const hoje = new Date();
                    setDataInicio(new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0]);
                    setDataFim(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0]);
                  }}
                >
                  Mês Atual
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const hoje = new Date();
                    setDataInicio(new Date(hoje.getFullYear(), 0, 1).toISOString().split('T')[0]);
                    setDataFim(new Date(hoje.getFullYear(), 11, 31).toISOString().split('T')[0]);
                  }}
                >
                  Ano Atual
                </Button>
                <Button
                  size="sm"
                  onClick={handleGerarPDF}
                  disabled={generatingPdf || filteredItems.length === 0}
                  className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"
                  data-testid="btn-gerar-pdf-relatorio"
                  title={filteredItems.length === 0 ? "Nenhum lançamento para exportar" : "Gerar relatório em PDF"}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  {generatingPdf ? "Gerando…" : "Gerar PDF"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGerarPDFPorCliente}
                  disabled={generatingPdf || filteredItems.length === 0}
                  className="border-luvcorp-navy text-luvcorp-navy hover:bg-luvcorp-navy/5"
                  data-testid="btn-gerar-pdf-por-cliente"
                  title={`Relatório agrupado por ${isReceber ? "cliente" : "fornecedor"} com subtotais — ideal para cobrança`}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  PDF por {isReceber ? "Cliente" : "Fornecedor"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGerarPDFConsolidado}
                  disabled={generatingPdf}
                  className="border-luvcorp-navy text-luvcorp-navy hover:bg-luvcorp-navy/5"
                  data-testid="btn-gerar-pdf-consolidado"
                  title="Gerar relatório único com Contas a Pagar E Receber juntos"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  PDF Consolidado
                </Button>
              </div>
            </div>
            
            {/* Filtro Rápido por Fornecedor/Cliente (Ranking) */}
            {ranking.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="ranking-entidades">
                <span className="text-xs font-semibold text-slate-600 mr-1">
                  Top {isReceber ? "Clientes" : "Fornecedores"} em aberto:
                </span>
                <button
                  type="button"
                  onClick={() => setFiltroEntidade("")}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition border ${filtroEntidade === "" ? "bg-luvcorp-navy text-white border-luvcorp-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                  data-testid="ranking-todos"
                >
                  Todos
                </button>
                {ranking.slice(0, 6).map((e) => {
                  const ativo = filtroEntidade === e.id;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setFiltroEntidade(ativo ? "" : e.id)}
                      title={`${e.qtd_pendentes} em aberto${e.qtd_vencidas ? ` · ${e.qtd_vencidas} vencidas` : ''}`}
                      className={`group px-2.5 py-1 rounded-full text-xs font-medium transition border flex items-center gap-1.5 ${ativo ? "bg-luvcorp-navy text-white border-luvcorp-navy" : "bg-white text-slate-700 border-slate-200 hover:border-luvcorp-navy/40"}`}
                      data-testid={`ranking-ent-${e.id}`}
                    >
                      <span className="truncate max-w-[180px]">{e.nome}</span>
                      <span className={`font-mono ${ativo ? "text-amber-200" : "text-slate-500 group-hover:text-luvcorp-navy"}`}>
                        {formatCurrency(e.total)}
                      </span>
                      {e.qtd_vencidas > 0 && (
                        <span className={`text-[10px] px-1 rounded ${ativo ? "bg-red-400/30 text-red-100" : "bg-red-100 text-red-700"}`}>
                          {e.qtd_vencidas}v
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Busca e Status */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={isReceber
                    ? "Buscar por cliente, nº de proposta, descrição, parcela…"
                    : "Buscar por fornecedor, descrição, documento, parcela…"}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-contas"
                />
              </div>
              <NativeSelect
                value={filterStatus}
                onValueChange={setFilterStatus}
                placeholder="Status"
                options={[
                  { value: "all", label: "Todos" },
                  { value: "pendente", label: "Pendente" },
                  { value: "pago", label: "Pago" },
                  { value: "cancelado", label: "Cancelado" }
                ]}
                className="w-[180px]"
              />
              <button
                type="button"
                onClick={() => setFiltroSemConta(!filtroSemConta)}
                className={`px-3 py-2 rounded-md text-xs font-medium border transition whitespace-nowrap ${filtroSemConta ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}
                title="Lançamentos pagos sem conta bancária identificada (legado)"
                data-testid="filtro-sem-conta"
              >
                ⚠ Sem conta identificada
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="bulk-bar">
          <p className="text-sm text-amber-800">
            <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? "lançamento selecionado" : "lançamentos selecionados"}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="btn-bulk-clear">
              Limpar seleção
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => { setBulkPassword(""); setShowBulkDelete(true); }}
              data-testid="btn-bulk-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Excluir selecionados
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <LancamentosTable
        items={filteredItems}
        isReceber={isReceber}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onBaixa={openBaixaDialog}
        onView={setViewItem}
        onEdit={(item) => { setEditItem(item); setShowForm(true); }}
        onDelete={(item) => { setDeleteItem(item); setDeletePassword(""); setShowDeleteDialog(true); }}
        onReverter={(item) => setReverterItem(item)}
      />

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle>
              {editItem ? "Editar Lançamento" : `Novo Lançamento - ${isReceber ? "A Receber" : "A Pagar"}`}
            </DialogTitle>
          </DialogHeader>
          <LancamentoForm 
            tipo={tipoLancamento}
            item={editItem}
            centrosCusto={centrosCusto}
            contas={contas}
            categorias={categorias}
            bancos={bancos}
            onSuccess={() => { setShowForm(false); fetchItems(); }}
            onCancel={() => setShowForm(false)}
            onCategoriaCreated={fetchDependencies}
          />
        </DialogContent>
      </Dialog>

      {/* Visualizar Dialog */}
      <ViewLancamentoDialog item={viewItem} onClose={() => setViewItem(null)} contas={contas} centrosCusto={centrosCusto} />

      {/* Excluir Dialogs (com senha) */}
      <DeleteLancamentoDialog
        open={showDeleteDialog}
        onOpenChange={(o) => { setShowDeleteDialog(o); if (!o) setDeletePassword(""); }}
        item={deleteItem}
        password={deletePassword}
        setPassword={setDeletePassword}
        deleting={deleting}
        onConfirm={handleConfirmDelete}
      />
      <BulkDeleteDialog
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        count={selectedIds.size}
        password={bulkPassword}
        setPassword={setBulkPassword}
        deleting={bulkDeleting}
        onConfirm={handleBulkDeleteConfirm}
      />

      {/* Baixa Dialog */}
      <BaixaDialog
        open={showBaixaDialog}
        onOpenChange={setShowBaixaDialog}
        item={baixaItem}
        baixaData={baixaData}
        setBaixaData={setBaixaData}
        bancos={bancos}
        isReceber={isReceber}
        onConfirm={handleBaixa}
      />

      {/* Reverter Baixa Dialog */}
      <Dialog open={!!reverterItem} onOpenChange={(o) => { if (!o) setReverterItem(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-reverter-baixa">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Undo2 className="h-5 w-5" />
              Reverter Baixa
            </DialogTitle>
          </DialogHeader>
          {reverterItem && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border text-sm space-y-1">
                <p><span className="text-slate-500">Descrição:</span> <strong>{reverterItem.descricao}</strong></p>
                {reverterItem.parcela && <p><span className="text-slate-500">Parcela:</span> {reverterItem.parcela}</p>}
                <p><span className="text-slate-500">Valor:</span> <strong>{formatCurrency(reverterItem.valor)}</strong></p>
                {reverterItem.data_pagamento && (
                  <p><span className="text-slate-500">{isReceber ? "Recebido" : "Pago"} em:</span> {new Date(reverterItem.data_pagamento + "T12:00:00").toLocaleDateString("pt-BR")}</p>
                )}
              </div>
              <p className="text-sm text-slate-600">
                O lançamento voltará para <strong>Pendente</strong> e os dados do pagamento
                (data, valor pago, juros/multa/desconto) serão limpos.
                {reverterItem.residuo_id && (
                  <span className="block mt-1 text-amber-700">⚠ Esta baixa foi parcial — o resíduo pendente gerado também será removido.</span>
                )}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReverterItem(null)} data-testid="btn-cancelar-reversao">
                  Cancelar
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleReverterBaixa}
                  disabled={reverting}
                  data-testid="btn-confirmar-reversao"
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  {reverting ? "Revertendo…" : "Reverter baixa"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transferência entre Contas */}
      <TransferenciaDialog
        open={showTransfer}
        onOpenChange={setShowTransfer}
        transferData={transferData}
        setTransferData={setTransferData}
        bancos={bancos}
        transferring={transferring}
        onConfirm={handleTransferencia}
      />

      {/* PDF Report Preview */}
      <PdfPreviewDialog pdfModal={pdfModal} onClose={closePdfModal} isReceber={isReceber} />

    </div>
  );
}
export default function ContasPagarReceberPage() {
  return (
    <MainLayout>
      <Tabs defaultValue="receber" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="receber" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-700">
            <ArrowUpRight className="h-4 w-4 mr-2" />
            A Receber
          </TabsTrigger>
          <TabsTrigger value="pagar" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
            <ArrowDownRight className="h-4 w-4 mr-2" />
            A Pagar
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="receber">
          <ContasPage tipo="receber" />
        </TabsContent>
        
        <TabsContent value="pagar">
          <ContasPage tipo="pagar" />
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
}
